import { useId, type ReactNode } from 'react'

import { IconButton } from '@/components/ui/Button'
import { useDismissable } from '@/hooks/useDismissable'
import { cn } from '@/lib/cn'

const CloseIcon = () => (
  <svg viewBox="0 0 16 16" className="h-4 w-4" aria-hidden="true">
    <path
      d="M4 4l8 8M12 4l-8 8"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      fill="none"
    />
  </svg>
)

/**
 * The scrim behind a dialog.
 *
 * A plain element, not a button. Making it a button — as both overlays used to
 * — puts a full-screen tab stop *before* the dialog, so the first Tab after
 * opening lands on an invisible control whose only announced name is "Close".
 * Escape and the explicit close button are the keyboard route.
 */
function Scrim({ onClick }: { onClick: () => void }) {
  return (
    <div
      aria-hidden="true"
      onClick={onClick}
      className="animate-in absolute inset-0 bg-black/45 backdrop-blur-[2px]"
    />
  )
}

interface OverlayHeaderProps {
  titleId: string
  title: ReactNode
  subtitle?: ReactNode
  onClose: () => void
  identifier?: boolean
}

function OverlayHeader({
  titleId,
  title,
  subtitle,
  onClose,
  identifier,
}: OverlayHeaderProps) {
  return (
    <header className="flex items-start justify-between gap-3 border-b border-line px-4 py-3">
      <div className="min-w-0">
        <h2
          id={titleId}
          className={cn(
            'truncate text-lg font-semibold tracking-[-0.01em] text-ink',
            identifier && 'ident',
          )}
        >
          {title}
        </h2>
        {subtitle ? (
          <p className="mt-0.5 truncate text-xs text-ink-secondary">{subtitle}</p>
        ) : null}
      </div>
      <IconButton label="Close" variant="ghost" onClick={onClose}>
        <CloseIcon />
      </IconButton>
    </header>
  )
}

export interface DrawerProps {
  open: boolean
  onClose: () => void
  title: string
  subtitle?: string
  children: ReactNode
  footer?: ReactNode
  widthClassName?: string
}

/** Right-hand side panel. Full width on small screens, a panel on large. */
export function Drawer({
  open,
  onClose,
  title,
  subtitle,
  children,
  footer,
  widthClassName = 'sm:max-w-xl',
}: DrawerProps) {
  const titleId = useId()
  const ref = useDismissable<HTMLElement>({
    open,
    onClose: () => onClose(),
    trapFocus: true,
    lockScroll: true,
  })

  if (!open) return null

  return (
    <div className="fixed inset-0 z-(--z-overlay) flex justify-end">
      <Scrim onClick={onClose} />
      <aside
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={cn(
          'animate-slide-in relative flex h-full w-full flex-col border-l border-line bg-surface shadow-lg',
          'focus-visible:outline-none',
          widthClassName,
        )}
      >
        <OverlayHeader
          titleId={titleId}
          title={title}
          subtitle={subtitle}
          onClose={onClose}
          identifier
        />
        <div className="scrollbar-slim min-h-0 flex-1 overflow-y-auto">{children}</div>
        {footer ? (
          <footer className="border-t border-line bg-surface-raised px-4 py-3">
            {footer}
          </footer>
        ) : null}
      </aside>
    </div>
  )
}

export interface ModalProps {
  open: boolean
  onClose: () => void
  title: string
  description?: string
  children: ReactNode
  footer?: ReactNode
  widthClassName?: string
}

export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  widthClassName = 'max-w-4xl',
}: ModalProps) {
  const titleId = useId()
  const ref = useDismissable<HTMLDivElement>({
    open,
    onClose: () => onClose(),
    trapFocus: true,
    lockScroll: true,
  })

  if (!open) return null

  return (
    <div className="fixed inset-0 z-(--z-overlay) flex items-end justify-center sm:items-center sm:p-6">
      <Scrim onClick={onClose} />
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={cn(
          'animate-rise relative flex max-h-[92vh] w-full flex-col rounded-t-xl border border-line',
          'bg-surface shadow-lg focus-visible:outline-none sm:rounded-xl',
          widthClassName,
        )}
      >
        <OverlayHeader
          titleId={titleId}
          title={title}
          subtitle={description}
          onClose={onClose}
        />
        <div className="scrollbar-slim min-h-0 flex-1 overflow-auto">{children}</div>
        {footer ? (
          <footer className="flex flex-wrap items-center justify-end gap-2 border-t border-line bg-surface-raised px-4 py-3">
            {footer}
          </footer>
        ) : null}
      </div>
    </div>
  )
}
